import { css, cx } from '@emotion/css';
import { useEffect, type HTMLAttributes } from 'react';
import { QrCode } from 'lucide-react';

const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-lg)',
});

const infoBox = css({
  padding: 'var(--spacing-md)',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.3)',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
  borderRadius: 'var(--spacing-sm)',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--spacing-md)',
});

const iconWrapper = css({
  width: '48px',
  height: '48px',
  minWidth: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'var(--pc-600)',
  borderRadius: 'var(--spacing-sm)',
  color: 'var(--primary-color)',
});

const infoContent = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xs)',
});

const infoTitle = css({
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  color: 'var(--pc-800)',
  margin: 0,
});

const infoText = css({
  fontSize: 'var(--fs-350)',
  color: 'var(--g-700)',
  margin: 0,
  lineHeight: 1.5,
});

const stepsList = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
  marginTop: 'var(--spacing-md)',
});

const step = css({
  display: 'flex',
  gap: 'var(--spacing-sm)',
  alignItems: 'flex-start',
});

const stepNumber = css({
  minWidth: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'var(--pc-600)',
  color: 'var(--primary-color)',
  borderRadius: '50%',
  fontSize: 'var(--fs-300)',
  fontWeight: 600,
});

const stepText = css({
  fontSize: 'var(--fs-350)',
  color: 'var(--g-700)',
  paddingTop: '2px',
});

export interface PixData {
  paymentMethodType: 'pix';
}

export interface PaymentMethodPixProps extends HTMLAttributes<HTMLDivElement> {
  onDataChange?: (data: PixData, isValid: boolean) => void;
}

export function PaymentMethodPix({
  onDataChange,
  className,
  style,
  ...props
}: PaymentMethodPixProps) {
  // PIX is always valid since no form input is required
  // Notify parent on mount
  useEffect(() => {
    onDataChange?.({ paymentMethodType: 'pix' }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx(container, className)} style={style} {...props}>
      <div className={infoBox}>
        <div className={iconWrapper}>
          <QrCode size={24} />
        </div>
        <div className={infoContent}>
          <h4 className={infoTitle}>Pagamento via PIX</h4>
          <p className={infoText}>
            Após confirmar o pedido, você receberá um QR Code para realizar o pagamento.
            O pagamento via PIX é instantâneo e seguro.
          </p>
        </div>
      </div>

      <div className={stepsList}>
        <div className={step}>
          <div className={stepNumber}>1</div>
          <p className={stepText}>
            Clique em "Finalizar Compra" para gerar o QR Code do PIX
          </p>
        </div>
        <div className={step}>
          <div className={stepNumber}>2</div>
          <p className={stepText}>
            Abra o aplicativo do seu banco e selecione a opção PIX
          </p>
        </div>
        <div className={step}>
          <div className={stepNumber}>3</div>
          <p className={stepText}>
            Escaneie o QR Code ou copie o código PIX gerado
          </p>
        </div>
        <div className={step}>
          <div className={stepNumber}>4</div>
          <p className={stepText}>
            Confirme o pagamento no seu aplicativo bancário
          </p>
        </div>
      </div>
    </div>
  );
}

export default PaymentMethodPix;
